'use client';

import { useMemo, useCallback } from 'react';
import { Configure, Hits } from 'react-instantsearch';
import { InstantSearchProvider } from '@/app/providers';
import { ALGOLIA_CONFIG, handleScrollToElement } from '@/app/lib';
import type { ArticlesList } from '@/sanity.types';
import { ArticleTypeEnum } from '@workspace/constants/article.constant';
import {
  CaseStudiesPageItemHit,
  AskGregSection,
  HeroBanner,
  ContactSupportSection,
  AlgoliaPagination,
  AlgoliaMenuSelect,
  NoResultsBoundary,
  AlgoliaSearchBox,
  AlgoliaClearRefinements,
  TagRefinements,
} from '@workspace/widgets';
import { Typography } from '@workspace/ui/common';

interface ClientBlogsPageProps {
  articlesListConfig: ArticlesList;
  articleType: ArticleTypeEnum;
  noResultsMessage: string;
}

export default function ClientBlogsPage({ articlesListConfig, articleType, noResultsMessage }: ClientBlogsPageProps) {
  const pageHeroBanner = articlesListConfig?.heroBanner as any;

  const contactSupportLocator = useMemo(() => ({ pageType: 'articlesList', slug: articleType }), [articleType]);
  const isSkipContactSupport = useMemo(
    () => !articlesListConfig?.isIncludedContactSupport,
    [articlesListConfig?.isIncludedContactSupport],
  );

  const quickPrompts = useMemo(() => {
    if (!Array.isArray(articlesListConfig?.askGreg?.prompts)) return undefined;
    return (articlesListConfig.askGreg.prompts || []).map((text: string) => ({
      text,
    }));
  }, [articlesListConfig?.askGreg?.prompts]);

  const handlePageChange = useCallback(() => {
    handleScrollToElement({
      fromId: 'article-list',
      toClassName: '.hero-banner-container',
    });
  }, []);

  return (
    <InstantSearchProvider indexName={ALGOLIA_CONFIG.LIST_INSTANT_SEARCH_INDEX.Articles}>
      <Configure filters={`articleType:${articleType}`} />
      <HeroBanner heroBannerData={pageHeroBanner} disableDefaultFallback />

      <div id="article-list" className="wrapper lg:py-3xl px-md">
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-6 pt-8 md:pt-0">
          <div className="w-full md:max-w-122">
            <Typography variant="bodyMd" className="mb-4 text-black font-semibold">
              {articlesListConfig?.ui?.searchIntroText}
            </Typography>
            <AlgoliaSearchBox placeholder={articlesListConfig?.ui?.searchPlaceholder} />
          </div>

          {articlesListConfig?.ui?.filters && articlesListConfig?.ui?.filters?.length > 0 && (
            <div className="flex flex-col w-full md:max-w-104">
              <Typography variant="bodyMd" className="mb-4 text-black font-semibold">
                {articlesListConfig.ui.filterSectionLabel}
              </Typography>
              <div className="grid grid-cols-2 gap-3 md:flex md:gap-4 justify-between flex-wrap w-full">
                {articlesListConfig.ui.filters.map((filter) => (
                  <AlgoliaMenuSelect
                    key={filter._key}
                    menuProps={{ attribute: `${filter.options}.name` }}
                    placeholder={filter.placeholder || 'Select'}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        <NoResultsBoundary fallback={null}>
          <div className="flex md:justify-center justify-start">
            <TagRefinements className="pt-lg max-w-76 sm:max-w-none" attribute="articleTags.name" sortBy={['name']} />
          </div>
        </NoResultsBoundary>
        <NoResultsBoundary fallback={null}>
          <AlgoliaClearRefinements className="pt-sm md:pt-md flex justify-center pb-lg md:pb-0" />
        </NoResultsBoundary>

        <NoResultsBoundary
          fallback={
            <div className="mx-auto max-w-66 sm:max-w-93.25 flex flex-col flex-1 items-center gap-sm text-center mt-2xl pt-lg md:pt-0 mb-45 md:mt-30 md:mb-80">
              <Typography variant="h6" className="whitespace-nowrap">
                {noResultsMessage}
              </Typography>
              <Typography variant="bodyMd">You might want to reset or change your filters</Typography>
              <AlgoliaClearRefinements
                translations={{ resetButtonText: 'Clear filters' }}
                className="flex justify-center"
              />
            </div>
          }
        >
          <Hits
            hitComponent={CaseStudiesPageItemHit}
            classNames={{
              list: 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-lg md:gap-x-5 md:gap-y-2xl place-items-center md:place-items-start xl:place-items-start md:mt-2xl',
              item: 'w-full',
            }}
          />
          <AlgoliaPagination
            onPageChange={handlePageChange}
            variant="jumper"
            className="mt-8 md:mt-2xl flex-col md:flex-row items-center justify-center mb-lg md:mb-0 gap-2"
            showItemsPerPageSelector={articlesListConfig?.ui?.showItemsPerPageSelector}
          />
        </NoResultsBoundary>
      </div>

      {articlesListConfig?.askGreg?.isEnabled && (
        <AskGregSection
          title={articlesListConfig?.askGreg?.heading || undefined}
          description={articlesListConfig?.askGreg?.description || undefined}
          quickPromptsTitle={articlesListConfig?.askGreg?.subheading || undefined}
          quickPrompts={quickPrompts}
          isShowButton={articlesListConfig?.askGreg?.isShowButton ?? undefined}
          backgroundColor={articlesListConfig?.askGreg?.backgroundColor || undefined}
        />
      )}

      <ContactSupportSection locator={contactSupportLocator} isSkip={isSkipContactSupport} showError />
    </InstantSearchProvider>
  );
}
